import React, { useState, useEffect } from 'react'
import Grid from '@mui/material/Grid'

import { useTranslation } from 'react-i18next'
import StatisticsBox from '../../components/statisticsBox/StatisticsBox'
import { getContentContainer } from '../../container/statistics-module'
import {
  IUserCaloriesService,
  IUserStepsService,
  IUserHeartService,
  IUserIntensityService,
  IUserSleepService,
  IUserSpO2Service,
  USER_CALORIES_SERVICE_KEY,
  USER_STEPS_SERVICE_KEY,
  USER_SLEEP_SERVICE_KEY,
  USER_SPO2_SERVICE_KEY,
  USER_HEART_SERVICE_KEY,
  USER_INTENSITY_SERVICE_KEY,
} from '../../modules/statistics'
import { Query } from '../../common/api/Query'
import { Statistics as ST } from '../../modules/statistics/models/Statistics'
import { Intensity as I } from '../../modules/statistics/models/Intensity'
import { getUserContainer } from '../../container/user-module'
import { ILoggedUserService } from '../../modules/users/services/LoggedUserService'
import { LOGGED_USER_SERVICE_KEY } from '../../modules/users'
import { emptyList, ItemList } from '../../common/models/ItemList'
import { PageLayout } from 'pages/layout'
import { WearableAssignment } from '../dashboard/WearableAssignment'

const aggregateDailySleep = (items: ST[]): ST[] => {
  const dailyMap = items.reduce((acc, item) => {
    const day = new Date(item.date).toISOString().slice(0, 10) // YYYY-MM-DD in UTC
    const existing = acc.get(day)
    if (existing) {
      existing.value += item.value
    } else {
      acc.set(day, {
        id: item.id,
        value: item.value,
        userId: item.userId,
        date: new Date(day + 'T00:00:00Z'),
      })
    }
    return acc
  }, new Map<string, { id: string; value: number; userId: string; date: Date }>())

  return Array.from(dailyMap.values())
    .map((dto) => new ST(dto))
    .sort((a, b) => a.date.getTime() - b.date.getTime())
}

type StatisticsProps = {
  id: string
  stylesText?: string
  stylesHistorial?: string
  styleInfoBox?: string
  dashboard?: boolean
}

const loggedUserService = getUserContainer().get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)

export function Statistics(props: StatisticsProps) {
  const { t } = useTranslation()

  const [calories, setCalories] = useState<ItemList<ST>>(emptyList())
  const [steps, setSteps] = useState<ItemList<ST>>(emptyList())
  const [sleep, setSleep] = useState<ItemList<ST>>(emptyList())
  const [spO2, setSpO2] = useState<ItemList<ST>>(emptyList())
  const [heart, setHeart] = useState<ItemList<ST>>(emptyList())
  const [intensity, setIntensity] = useState<ItemList<I>>(emptyList())

  const caloriesService = getContentContainer().get<IUserCaloriesService>(USER_CALORIES_SERVICE_KEY)
  const stepsService = getContentContainer().get<IUserStepsService>(USER_STEPS_SERVICE_KEY)
  const sleepService = getContentContainer().get<IUserSleepService>(USER_SLEEP_SERVICE_KEY)
  const spO2Service = getContentContainer().get<IUserSpO2Service>(USER_SPO2_SERVICE_KEY)
  const heartService = getContentContainer().get<IUserHeartService>(USER_HEART_SERVICE_KEY)
  const intensityService = getContentContainer().get<IUserIntensityService>(
    USER_INTENSITY_SERVICE_KEY
  )
  const loggedUser = loggedUserService.get()
  const [isLoading] = useState<boolean>(true)

  useEffect(() => {
    if (isLoading && loggedUser?.id) {
      caloriesService
        .getFilteredList(
          new Query({
            query: [
              {
                name: 'userId',
                value: props.id ? props.id : loggedUser.id || '',
              },
            ],
          })
        )
        .subscribe((res) => {
          if (!res) return
          setCalories(res)
        })
      stepsService
        .getFilteredList(
          new Query({
            query: [
              {
                name: 'userId',
                value: props.id ? props.id : loggedUser.id || '',
              },
            ],
          })
        )
        .subscribe((res) => {
          if (!res) return
          setSteps(res)
        })

      sleepService
        .getFilteredList(
          new Query({
            query: [
              {
                name: 'userId',
                value: props.id ? props.id : loggedUser.id || '',
              },
            ],
          })
        )
        .subscribe((res) => {
          if (!res) return
          setSleep({
            ...res,
            items: aggregateDailySleep(res.items),
          })
        })

      spO2Service
        .getFilteredList(
          new Query({
            query: [
              {
                name: 'userID',
                value: props.id ? props.id : loggedUser.id || '',
              },
            ],
          })
        )
        .subscribe((res) => {
          if (!res) return
          setSpO2(res)
        })

      heartService
        .getFilteredList(
          new Query({
            query: [
              {
                name: 'userId',
                value: props.id ? props.id : loggedUser.id || '',
              },
            ],
          })
        )
        .subscribe((res) => {
          if (!res) return
          setHeart(res)
        })

      intensityService
        .getFilteredList(
          new Query({
            query: [
              {
                name: 'userId',
                value: props.id ? props.id : loggedUser.id || '',
              },
            ],
          })
        )
        .subscribe((res) => {
          if (!res) return
          setIntensity(res)
        })
    }
  }, [])

  if (props.dashboard) {
    return (
      <Grid container pt={2} spacing={{ xs: 4, md: 3 }} columns={12}>
        <Grid item xs={12} sm={6} md={6} xl={4}>
          <StatisticsBox
            dashboard={props.dashboard}
            stylesHistorial={props.stylesHistorial}
            stylesText={props.stylesText}
            styleInfoBox={props.styleInfoBox}
            titulo={t('stepsDay')}
            data={steps.items}
            dataSetsNames={[t('steps')]}
            tipoChart="line"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6} xl={4}>
          <StatisticsBox
            dashboard={props.dashboard}
            stylesHistorial={props.stylesHistorial}
            styleInfoBox={props.styleInfoBox}
            stylesText={props.stylesText}
            titulo={t('intensityDay')}
            data={intensity.items}
            dataSetsNames={[
              t('sedentaryMin'),
              t('lightlyActiveMin'),
              t('fairlyActiveMin'),
              t('veryActiveMin'),
            ]}
            tipoChart="stackedBar"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6} xl={4}>
          <StatisticsBox
            dashboard={props.dashboard}
            stylesHistorial={props.stylesHistorial}
            styleInfoBox={props.styleInfoBox}
            stylesText={props.stylesText}
            titulo={t('restingHeartRate')}
            data={heart.items}
            dataSetsNames={['Heart Rate']}
            tipoChart="line"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6} xl={4}>
          <StatisticsBox
            dashboard={props.dashboard}
            stylesHistorial={props.stylesHistorial}
            styleInfoBox={props.styleInfoBox}
            stylesText={props.stylesText}
            titulo={t('hoursSleep')}
            data={sleep.items}
            dataSetsNames={['Sleep']}
            tipoChart="line"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6} xl={4}>
          <StatisticsBox
            dashboard={props.dashboard}
            stylesHistorial={props.stylesHistorial}
            styleInfoBox={props.styleInfoBox}
            stylesText={props.stylesText}
            titulo={t('caloriesDay')}
            data={calories.items}
            dataSetsNames={['Calories']}
            tipoChart="line"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6} xl={4}>
          <StatisticsBox
            dashboard={props.dashboard}
            stylesHistorial={props.stylesHistorial}
            styleInfoBox={props.styleInfoBox}
            stylesText={props.stylesText}
            titulo={t('spO2')}
            data={spO2.items}
            dataSetsNames={[t('spO2')]}
            tipoChart="line"
          />
        </Grid>
      </Grid>
    )
  }

  return (
    <PageLayout withCard={false} scrollable>
      <Grid container spacing={2} style={{ padding: '32px 32px 0 32px' }}>
        <Grid item xs={12} sm={6} md={4}>
          <WearableAssignment userId={props.id || loggedUser?.id || ''} />
        </Grid>
        <Grid item xs={12} sm={6} md={8} />
      </Grid>

      <Grid container pt={4} pl={4} pr={4} spacing={{ xs: 4, md: 3 }} columns={12}>
        <Grid item xs={12} sm={6} md={4}>
          <StatisticsBox
            stylesHistorial={props.stylesHistorial}
            stylesText={props.stylesText}
            styleInfoBox={props.styleInfoBox}
            titulo={t('stepsDay')}
            data={steps.items}
            dataSetsNames={[t('steps')]}
            tipoChart="line"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatisticsBox
            stylesHistorial={props.stylesHistorial}
            styleInfoBox={props.styleInfoBox}
            stylesText={props.stylesText}
            titulo={t('intensityDay')}
            data={intensity.items}
            dataSetsNames={[
              t('sedentaryMin'),
              t('lightlyActiveMin'),
              t('fairlyActiveMin'),
              t('veryActiveMin'),
            ]}
            tipoChart="stackedBar"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatisticsBox
            stylesHistorial={props.stylesHistorial}
            styleInfoBox={props.styleInfoBox}
            stylesText={props.stylesText}
            titulo={t('restingHeartRate')}
            data={heart.items}
            dataSetsNames={['Heart Rate']}
            tipoChart="line"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatisticsBox
            stylesHistorial={props.stylesHistorial}
            styleInfoBox={props.styleInfoBox}
            stylesText={props.stylesText}
            titulo={t('hoursSleep')}
            data={sleep.items}
            dataSetsNames={['Sleep']}
            tipoChart="line"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatisticsBox
            stylesHistorial={props.stylesHistorial}
            styleInfoBox={props.styleInfoBox}
            stylesText={props.stylesText}
            titulo={t('caloriesDay')}
            data={calories.items}
            dataSetsNames={['Calories']}
            tipoChart="line"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatisticsBox
            stylesHistorial={props.stylesHistorial}
            styleInfoBox={props.styleInfoBox}
            stylesText={props.stylesText}
            titulo={t('spO2')}
            data={spO2.items}
            dataSetsNames={[t('spO2')]}
            tipoChart="line"
          />
        </Grid>
      </Grid>
    </PageLayout>
  )
}
